import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { wibToday } from "@/lib/bizdate";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { rupiah, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Banknote, Plus } from "lucide-react";

export default function MarketplaceSettlement() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => { setRows(null); api.get("/sales/settlements").then((r) => setRows(r.data)); };
  useEffect(load, []);
  const cancel = async (id) => { try { await api.post(`/sales/settlements/${id}/cancel`); toast.success("Settlement dibatalkan"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div>
      <PageHeader title="Settlement Marketplace" subtitle="Pencairan dana marketplace terhadap piutang. Tidak membuat pendapatan baru (Dr Kas/Bank, Cr Piutang)." testId="settlement-header">
        {can("ar.settlement.manage") && <Button onClick={() => setOpen(true)} data-testid="add-settlement-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" />Settlement Baru</Button>}
      </PageHeader>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Belum ada settlement" icon={Banknote} /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>No Settlement</TableHead><TableHead>Marketplace</TableHead><TableHead>Tanggal</TableHead><TableHead>Kas/Bank</TableHead>
              <TableHead className="text-right">Order</TableHead><TableHead className="text-right">Piutang</TableHead>
              <TableHead className="text-right">Kas Diterima</TableHead><TableHead className="text-right">Penyesuaian</TableHead>
              <TableHead>Jurnal</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((s) => (
                <TableRow key={s.id} data-testid={`settlement-row-${s.settlement_no}`}>
                  <TableCell className="font-mono text-xs">{s.settlement_no}</TableCell>
                  <TableCell>{s.marketplace_name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{formatDate(s.settlement_date)}</TableCell>
                  <TableCell className="text-xs">{s.cash_bank_name}</TableCell>
                  <TableCell className="text-right">{s.allocations?.length || 0}</TableCell>
                  <TableCell className="text-right tabular-nums">{rupiah(s.total_ar)}</TableCell>
                  <TableCell className="text-right tabular-nums font-medium text-emerald-600">{rupiah(s.cash_received)}</TableCell>
                  <TableCell className="text-right tabular-nums text-slate-500">{rupiah(s.adjustment)}</TableCell>
                  <TableCell><StatusBadge status={s.journal_status} /></TableCell>
                  <TableCell><StatusBadge status={s.status} /></TableCell>
                  <TableCell className="text-right">{s.status === "Tercatat" && s.journal_status === "Draft" && can("ar.settlement.manage") && <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-500" onClick={() => cancel(s.id)}>Batal</Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {open && <SettlementDialog onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function SettlementDialog({ onClose, onSaved }) {
  const today = wibToday();
  const [marketplaces, setMarketplaces] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [mkId, setMkId] = useState("");
  const [recs, setRecs] = useState([]);
  const [sel, setSel] = useState({}); // receivable_id -> amount
  const [f, setF] = useState({ cash_bank_account_code: "", settlement_date: today, cash_received: "", reference: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/marketplaces").then((r) => setMarketplaces(r.data)).catch(() => {});
    api.get("/accounting/cash-bank").then((r) => setAccounts(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    setSel({});
    if (!mkId) { setRecs([]); return; }
    api.get(`/sales/receivables?marketplace_id=${mkId}&outstanding_only=true`).then((r) => setRecs(r.data));
  }, [mkId]);

  const toggle = (r) => setSel((prev) => { const n = { ...prev }; if (n[r.id] != null) delete n[r.id]; else n[r.id] = r.outstanding; return n; });
  const totalAr = Object.values(sel).reduce((s, v) => s + (Number(v) || 0), 0);
  const cashReceived = f.cash_received === "" ? totalAr : Number(f.cash_received);
  const adjustment = Math.round((totalAr - cashReceived) * 100) / 100;

  const save = async () => {
    const allocations = Object.entries(sel).map(([receivable_id, amount]) => ({ receivable_id, amount: Number(amount) }));
    if (allocations.length === 0) return toast.error("Pilih minimal 1 order");
    if (!f.cash_bank_account_code) return toast.error("Pilih akun Kas/Bank");
    for (const a of allocations) { const r = recs.find((x) => x.id === a.receivable_id); if (a.amount <= 0 || a.amount > r.outstanding + 1e-6) return toast.error(`Jumlah settle tidak valid untuk ${r.order_no}`); }
    setSaving(true);
    try {
      await api.post("/sales/settlements", {
        marketplace_id: mkId, cash_bank_account_code: f.cash_bank_account_code, settlement_date: f.settlement_date,
        allocations, cash_received: f.cash_received === "" ? null : Number(f.cash_received),
        reference: f.reference || null, notes: f.notes || null,
      });
      toast.success("Settlement dicatat (jurnal DRAFT)"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="settlement-dialog">
        <DialogHeader><DialogTitle className="font-display">Settlement Marketplace</DialogTitle><DialogDescription>Cairkan piutang dari beberapa order sekaligus ke akun Kas/Bank.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs text-slate-500">Marketplace</Label><Select value={mkId} onValueChange={setMkId}><SelectTrigger className="mt-1" data-testid="stl-marketplace"><SelectValue placeholder="Pilih..." /></SelectTrigger><SelectContent>{marketplaces.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs text-slate-500">Akun Kas/Bank</Label><Select value={f.cash_bank_account_code} onValueChange={(v) => setF({ ...f, cash_bank_account_code: v })}><SelectTrigger className="mt-1" data-testid="stl-account"><SelectValue placeholder="Pilih..." /></SelectTrigger><SelectContent>{accounts.map((a) => <SelectItem key={a.account_code} value={a.account_code}>{a.name}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs text-slate-500">Tanggal</Label><Input type="date" className="mt-1" value={f.settlement_date} onChange={(e) => setF({ ...f, settlement_date: e.target.value })} /></div>
        </div>
        <div className="rounded-lg border border-slate-200 overflow-x-auto">
          {!mkId ? <p className="p-4 text-sm text-slate-400">Pilih marketplace untuk melihat piutang outstanding.</p> : recs.length === 0 ? <p className="p-4 text-sm text-slate-400">Tidak ada piutang outstanding.</p> : (
            <Table>
              <TableHeader><TableRow><TableHead className="w-10"></TableHead><TableHead>Order</TableHead><TableHead>Tanggal</TableHead><TableHead className="text-right">Sisa Piutang</TableHead><TableHead className="text-right">Jumlah Settle</TableHead></TableRow></TableHeader>
              <TableBody>
                {recs.map((r) => (
                  <TableRow key={r.id} data-testid={`stl-rec-${r.order_no}`}>
                    <TableCell><Checkbox checked={sel[r.id] != null} onCheckedChange={() => toggle(r)} data-testid={`stl-check-${r.order_no}`} /></TableCell>
                    <TableCell className="font-mono text-xs">{r.order_no}</TableCell>
                    <TableCell className="text-xs text-slate-500">{formatDate(r.invoice_date)}</TableCell>
                    <TableCell className="text-right tabular-nums">{rupiah(r.outstanding)}</TableCell>
                    <TableCell className="text-right">{sel[r.id] != null && <Input type="number" className="h-8 w-28 ml-auto" value={sel[r.id]} onChange={(e) => setSel({ ...sel, [r.id]: e.target.value })} data-testid={`stl-amount-${r.order_no}`} />}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs text-slate-500">Kas Diterima (opsional)</Label><Input type="number" className="mt-1" placeholder={String(totalAr)} value={f.cash_received} onChange={(e) => setF({ ...f, cash_received: e.target.value })} data-testid="stl-cash" /></div>
          <div><Label className="text-xs text-slate-500">Referensi</Label><Input className="mt-1" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} /></div>
          <div><Label className="text-xs text-slate-500">Catatan</Label><Input className="mt-1" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-6 text-sm" data-testid="stl-totals">
          <span>Total Piutang: <b>{rupiah(totalAr)}</b></span>
          <span>Kas Diterima: <b className="text-emerald-600">{rupiah(cashReceived)}</b></span>
          <span className={adjustment !== 0 ? "text-amber-600" : "text-slate-400"}>Penyesuaian: <b>{rupiah(adjustment)}</b></span>
        </div>
        {adjustment !== 0 && <p className="text-[11px] text-amber-600">Selisih dicatat ke Biaya Marketplace (6950) bila kas &lt; piutang, atau Pendapatan Lain (4200) bila kas &gt; piutang.</p>}
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving || totalAr <= 0} data-testid="save-settlement-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Simpan Settlement"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
